import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { MarketplaceVisibilityService } from '../services/marketplace-visibility.service';

/**
 * "Available for Consultations" toggle — placed in the top nav bar,
 * beside the Customer Support icon.
 *
 * State is always driven by MarketplaceVisibilityService, which itself is
 * seeded from the login/profile API response (`is_marketplace_listed`).
 */
@Component({
  selector: 'app-available-toggle',
  templateUrl: './available-toggle.component.html',
  styleUrls: ['./available-toggle.component.scss'],
  standalone: false
})
export class AvailableToggleComponent implements OnInit, OnDestroy {

  isListed = true;
  isSaving = false;
  isHighlighted = false;

  private subs = new Subscription();

  constructor(
    private marketplaceService: MarketplaceVisibilityService,
    private dialog: MatDialog,
    private toast: ToastrService
  ) {}

  ngOnInit(): void {
    this.isListed = this.marketplaceService.isListed;

    this.subs.add(
      this.marketplaceService.isListed$.subscribe(val => (this.isListed = val))
    );

    this.subs.add(
      this.marketplaceService.highlightRequest$.subscribe(() => this.scrollAndPulse())
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  onToggleClick(): void {
    // Prevent duplicate calls while a request is already in flight
    // and gracefully absorb rapid repeated clicks.
    if (this.isSaving) return;

    if (this.isListed) {
      // Turning OFF: confirm first, do not flip the UI yet.
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        width: '400px',
        data: {
          title: 'Pause Incoming Consultations?',
          message:
            'Turning this off pauses incoming consultation requests. Keep it on to stay visible and grow your business!!',
          confirmText: 'Go Offline',
          cancelText: 'Stay Online',
          confirmVariant: 'orange',
          cancelVariant: 'green',
          icon: 'warning'
        }
      });

      dialogRef.afterClosed().subscribe((confirmed: any) => {
        if (confirmed) {
          this.persistVisibility(false);
        }
      });
    } else {
      // Turning ON: no confirmation required.
      this.persistVisibility(true);
    }
  }

  private persistVisibility(newValue: boolean): void {
    this.isSaving = true;

    this.marketplaceService.updateVisibility(newValue).subscribe({
      next: (res: any) => {
        this.isSaving = false;

        if (!res || res.error) {
          this.toast.error(res?.msg || 'Unable to update consultation availability. Please try again.');
          return; // toggle state untouched — restores to previous automatically
        }

        this.marketplaceService.setListed(newValue);
        this.toast.success(
          newValue
            ? 'You are now available for consultations.'
            : 'You are now offline. Incoming consultation requests have been paused.'
        );
      },
      error: () => {
        this.isSaving = false;
        this.toast.error('Unable to update consultation availability. Please try again.');
      }
    });
  }

  private scrollAndPulse(): void {
    const el = document.getElementById('marketplace-toggle-wrapper');
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });

    this.isHighlighted = true;
    setTimeout(() => {
      this.isHighlighted = false;
    }, 2000);
  }
}